/**
 * 무대 목표 편집기의 정답 시험(2026-10-08 · FS-15-08) — 편집 중인 기구 무대 모드 정의로 그 차시의 Bench 정답을 실제 로컬 호스트(커널)에서 돌린다.
 *  - 실행 묶음: 자산 서버 매니페스트의 모형(GLB · 관절 · 콜라이더) + 정답 프로그램(운영 API /api/ops/stage-answers) — 개발 /stage-check 와 같다
 *  - 아이 손: 커널 시험과 같은 일정(KIDS — 센서 손 · 허브 버튼)
 *  - 빨리 감기: 인라인 호스트에 빠른 시계(now × speed)를 준다(물리 틱은 그대로 — 결과는 1배속과 같다)
 */
import type {
  Frame,
  HandInput,
  LoadResult,
  LocalHost,
  ModeDefinition,
  RunBundle,
  RuntimeConfig,
} from "@picnic-field/local-host/protocol";
import { KIDS } from "../../../../../../libs/kernel/test/stage-kids";

export interface StageAnswers {
  readonly lessons: Readonly<
    Record<string, { readonly build_ref: string; readonly program: unknown }>
  >;
  /** 자산 서버(끝에 /) */
  readonly assetBase: string;
}

interface ManifestModel {
  slug: string;
  glbVersion: string;
  jointSpecVersion: string;
  sha256: string;
  glbPath: string;
  jointSpecPath: string;
  collidersPath: string;
  kinPath: string;
  physicsPreset: string;
}

/** 모형의 관절 목록(id · 포트) — 목표 편집 칸의 고르기 목록 */
export interface ModelJoint {
  readonly id: string;
  readonly port: string | null;
}

const manifests = new Map<string, Promise<{ models: ManifestModel[] }>>();
function manifest(base: string, fetchFn: typeof fetch) {
  let p = manifests.get(base);
  if (!p) {
    p = fetchFn(new URL("manifest.json", base).toString()).then((r) => {
      if (!r.ok) throw new Error(`manifest ${r.status}`);
      return r.json() as Promise<{ models: ManifestModel[] }>;
    });
    p.catch(() => manifests.delete(base));
    manifests.set(base, p);
  }
  return p;
}

async function modelOf(base: string, slug: string, fetchFn: typeof fetch) {
  const m = (await manifest(base, fetchFn)).models.find((x) => x.slug === slug);
  if (!m) throw new Error(`자산 매니페스트에 ${slug} 없음`);
  const kin = (await (await fetchFn(new URL(m.kinPath, base).toString())).json()) as {
    joints: ({ id?: string; port?: string } | string)[];
  };
  return { m, kin };
}

export async function modelJoints(
  base: string,
  slug: string,
  fetchFn: typeof fetch = fetch,
): Promise<ModelJoint[]> {
  const { kin } = await modelOf(base, slug, fetchFn);
  return kin.joints
    .map((j) =>
      typeof j === "string" ? { id: j, port: null } : { id: j.id ?? "", port: j.port ?? null },
    )
    .filter((j, i, all) => j.id && all.findIndex((x) => x.id === j.id) === i);
}

/** 정답 실행 묶음(받침대 기계 · static) — 개발 /stage-check 의 bundleOf 와 같은 모양 */
export async function stageBundle(
  base: string,
  slug: string,
  program: unknown,
  fetchFn: typeof fetch = fetch,
): Promise<RunBundle> {
  const { m, kin } = await modelOf(base, slug, fetchFn);
  const url = (p: string) => new URL(p, base).toString();
  const ports: Record<string, string> = {};
  for (const j of kin.joints)
    if (typeof j === "object" && j.id && j.port && !ports[j.port]) ports[j.port] = j.id;
  return {
    snapshotId: `snap_stage_goals_${slug}`,
    mdf: {
      mdfVersion: "1.0",
      model: { slug: m.slug, jointSpecVersion: m.jointSpecVersion, glbVersion: m.glbVersion },
      limits: { version: null },
      ports,
      physics: { preset: m.physicsPreset },
      appearance: { palette: "brick-v1", colors: {} },
      program: null,
      meta: { authorPid: "hmac:stage-goals" },
    },
    program: {
      format: "bench-ast@0",
      ast: program,
      versionHash: `sha256:${"5".repeat(64)}`,
      board: "spike",
    },
    static: true,
    asset: {
      slug: m.slug,
      glbVersion: m.glbVersion,
      jointSpecVersion: m.jointSpecVersion,
      sha256: m.sha256,
      glbUrl: url(m.glbPath),
      jointSpecUrl: url(m.jointSpecPath),
      collidersUrl: url(m.collidersPath),
      physicsPreset: m.physicsPreset,
    },
    appearance: null,
    versions: { compilerVersion: "", kinematicsVersion: "", engineBuild: "", runtimeVersion: "" },
  } as unknown as RunBundle;
}

export interface StageTestProgress {
  /** 시뮬레이션 초 */
  readonly simS: number;
  readonly stars: number;
  /** 별이 켜진 시뮬레이션 초(1 · 2 · 3번째) */
  readonly starAt: readonly number[];
  readonly goals: readonly { measure: string; value: number; target: number; done: boolean }[];
  readonly bonus: { value: number; target: number; done: boolean } | null;
  readonly done: boolean;
}

export interface StageTestRun {
  readonly load: LoadResult;
  readonly bundle: RunBundle;
  onFrame(cb: (f: Frame) => void): () => void;
  onProgress(cb: (p: StageTestProgress) => void): () => void;
  stop(): void;
}

export type StageHostFactory = (now: () => number) => Promise<LocalHost>;

/** 빠른 시계 — 실제 시각 × speed */
export function fastClock(speed: number, real: () => number = () => performance.now()) {
  const r0 = real();
  return () => r0 + (real() - r0) * speed;
}

/**
 * 편집 중인 정의(def)의 presetKey 무대에서 정답을 돌린다. 시간이 다 되면(timerS) done.
 * 아이 손은 지난 프레임 뒤 ~ 지금 사이 틱마다 보고 단추 · 힘은 한 번이라도 눌렸으면 누름(개발 /stage-check 와 같다).
 */
export async function startStageTest(opts: {
  createHost: StageHostFactory;
  def: ModeDefinition;
  presetKey: string;
  bundle: RunBundle;
  runtimeConfig: RuntimeConfig;
  speed: number;
}): Promise<StageTestRun> {
  const preset = opts.def.presets.find((p) => p.key === opts.presetKey) as
    { timerS: number; stage?: { lesson: string } } | undefined;
  if (!preset?.stage) throw new Error(`무대 ${opts.presetKey} 없음`);
  const host = await opts.createHost(fastClock(opts.speed));
  const load = await host.load({
    me: opts.bundle,
    mode: opts.def,
    presetKey: opts.presetKey,
    runtimeConfig: opts.runtimeConfig,
    seed: 1,
    ghosts: [],
  });
  const kid = KIDS[preset.stage.lesson] ?? {};
  const listeners = new Set<(p: StageTestProgress) => void>();
  const starAt: number[] = [];
  let simS = 0;
  let prevS = -1 / 60;
  let finished = false;
  const feed = () => {
    const ts: number[] = [];
    for (let t = prevS + 1 / 60; t <= simS + 1e-9; t += 1 / 60) ts.push(t);
    if (!ts.length) ts.push(simS);
    prevS = simS;
    const force = Math.max(0, ...ts.map((t) => kid.hand?.(t)?.force ?? 0));
    const h: HandInput = {
      pitch: 0,
      roll: 0,
      force: 0,
      color: -1,
      distance: -1,
      ...kid.hand?.(simS),
      ...(force > 0 ? { force } : {}),
    } as HandInput;
    host.hand?.(h);
    host.input({
      dpad: null,
      a: ts.some((t) => kid.left?.(t) ?? false),
      b: ts.some((t) => kid.right?.(t) ?? false),
      slider: 0,
    });
  };
  const offFrame = host.onFrame((f) => {
    if (finished) return;
    simS = f.tick / 60;
    feed();
    const st = f.stage;
    if (!st) return;
    while (starAt.length < st.stars) starAt.push(simS);
    const done = simS >= preset.timerS;
    const p: StageTestProgress = {
      simS,
      stars: st.stars,
      starAt: [...starAt],
      goals: st.goals.map((g) => ({ ...g })),
      bonus: st.bonus
        ? { value: st.bonus.value, target: st.bonus.target, done: st.bonus.done }
        : null,
      done,
    };
    listeners.forEach((l) => l(p));
    if (done) {
      finished = true;
      host.pause();
    }
  });
  feed();
  host.start();
  return {
    load,
    bundle: opts.bundle,
    onFrame: (cb) => host.onFrame(cb),
    onProgress(cb) {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    stop() {
      finished = true;
      offFrame();
      host.dispose();
    },
  };
}

/** 기본 호스트 — 인라인 로컬 호스트(빠른 시계를 받는다) · 시험은 setStageHostForTests 로 바꾼다 */
const inlineStageHost: StageHostFactory = async (now) => {
  const { createInlineHost } = await import("@picnic-field/local-host");
  return createInlineHost({ now });
};
let stageHost: StageHostFactory = inlineStageHost;
export const stageHostFactory = (): StageHostFactory => stageHost;
/** 시험 전용 — null 이면 기본값 */
export function setStageHostForTests(f: StageHostFactory | null) {
  stageHost = f ?? inlineStageHost;
}
